import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Send, Ban, Check, RotateCcw, Printer, FileDown, Edit3, Search } from "lucide-react";
import { EvidencePanel } from "../components/EvidencePanel";
import { CostCodeHeader, AccountPicker, CostCodeChip } from "../components/CostCode";

const EMPTY_LINE = { item_number: "", description: "", qty: 1, uom: "EA", warehouse: "", location: "", equipment_code: "", equipment_name: "", is_refaccion: false, category: "", cost_code: { subcuenta: "", tipo: "" } };
const EMPTY_FORM = { department: "", requester_id: "", requester_name: "", authorizer_id: "", notes: "", client_code: "", contract_code: "", frente_code: "", lines: [ { ...EMPTY_LINE } ] };

const ST_LABELS = {
  en_captura: "En captura",
  pendiente: "Pendiente",
  autorizado: "Autorizado",
  surtido: "Surtido",
  surtido_parcial: "Surtido parcial",
  rechazado_almacen: "Rechazado por almacén",
  rechazado: "Rechazado",
  cancelado: "Cancelado",
};
const ST_STATUS_MAP = {
  en_captura: "draft",
  pendiente: "pending",
  autorizado: "approved",
  surtido: "supplied",
  surtido_parcial: "partial",
  rechazado_almacen: "wh_rejected",
  rechazado: "rejected",
  cancelado: "cancelled",
};

function ItemSearch({ value, onSelect }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const h = setTimeout(() => {
      api.get("/items/search", { params: { q, limit: 8 } }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div className="relative">
      <div className="flex items-center gap-1 border rounded-none h-9 px-2 bg-background">
        <Search size={12} className="text-muted-foreground" />
        <input
          value={q}
          placeholder="Código, descripción o No. de parte…"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full text-xs bg-transparent outline-none"
          data-testid="vale-item-search"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 border bg-card shadow max-h-56 overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { onSelect(r); setQ(r.item_number); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 hover:bg-muted text-xs border-b last:border-b-0"
              data-testid={`vale-item-option-${r.item_number}`}
            >
              <span className="font-bold">{r.item_number}</span>
              {r.part_number && <span className="ml-1 font-mono text-[10px] text-amber-700 dark:text-amber-400">NP {r.part_number}</span>} — {r.description}
              {r.category && <span className="text-[10px] text-muted-foreground ml-1">· {r.category}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Vales() {
  const { t } = useI18n();
  const { user, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const [departments, setDepartments] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [authorizers, setAuthorizers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const [reasonAction, setReasonAction] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [supplyOpen, setSupplyOpen] = useState(false);
  const [supplyVale, setSupplyVale] = useState(null);
  const [supplyLines, setSupplyLines] = useState([]);

  const canCreate = can("admin", "buyer", "warehouse", "maintenance");
  const canAuthorize = can("admin", "approver", "warehouse");

  const load = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (q) params.q = q;
    api.get("/vales", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/personnel", { params: { active: true } }).then((r) => setPersonnel(r.data)).catch(() => {});
    api.get("/vales/authorizers").then((r) => setAuthorizers(r.data)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, lines: [{ ...EMPTY_LINE }] });
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditId(v.id);
    setForm({
      department: v.department || "",
      requester_id: v.requester_id || "",
      requester_name: v.requester_name || "",
      authorizer_id: v.authorizer_id || "",
      notes: v.notes || "",
      client_code: v.client_code || "", contract_code: v.contract_code || "", frente_code: v.frente_code || "",
      lines: (v.lines || []).map((l) => ({ ...EMPTY_LINE, ...l, cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" } })),
    });
    setDetailOpen(false);
    setOpen(true);
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { ...EMPTY_LINE }] });
  const removeLine = (idx) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
  const updateLine = (idx, patch) => setForm({
    ...form,
    lines: form.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  });

  const pickItem = (idx, item) => updateLine(idx, {
    item_number: item.item_number,
    description: item.description || "",
    uom: item.uom_primary || "EA",
    category: item.category || "",
    is_refaccion: /refac/i.test(item.category || ""),
  });

  const pickEquipment = (idx, eqCode) => {
    const eq = equipment.find((e) => e.code === eqCode);
    updateLine(idx, { equipment_code: eqCode, equipment_name: eq?.name || "" });
  };

  const save = async () => {
    try {
      const payload = {
        department: form.department,
        requester_id: form.requester_id,
        requester_name: form.requester_name,
        authorizer_id: form.authorizer_id,
        notes: form.notes,
        client_code: form.client_code, contract_code: form.contract_code, frente_code: form.frente_code,
        lines: form.lines.map((l) => ({
          item_number: l.item_number,
          description: l.description,
          qty: Number(l.qty),
          uom: l.uom,
          warehouse: l.warehouse,
          location: l.location,
          equipment_code: l.equipment_code,
          equipment_name: l.equipment_name,
          cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" },
        })),
      };
      let saved;
      if (editId) saved = (await api.put(`/vales/${editId}`, payload)).data;
      else saved = (await api.post("/vales", payload)).data;
      toast.success(`${t("common.saved")} · ${saved.number}`);
      setOpen(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doAction = async (id, action, body = {}) => {
    try {
      const { data } = await api.post(`/vales/${id}/${action}`, body);
      toast.success(`${data.number} · ${ST_LABELS[data.status]}`);
      load();
      if (detail?.id === id) setDetail(data);
      setReasonAction(null); setReasonText("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = async (v) => {
    try {
      const { data } = await api.get(`/vales/${v.id}`);
      setDetail(data);
      setDetailOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const printPdf = (v) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/vales/${v.id}/pdf`;
    const w = window.open(url, "_blank");
    if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) {} });
  };

  const downloadPdf = (v) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/vales/${v.id}/pdf`, "_blank");
  };

  const canEditDraft = (v) => v && (v.status === "en_captura" || v.status === "rechazado") &&
    (user?.role === "admin" || v.generator_email === user?.email);

  const canCancel = (v) => v && v.status !== "autorizado" && v.status !== "cancelado" &&
    (user?.role === "admin" || v.generator_email === user?.email || canAuthorize);

  const canAuthorizeThis = (v) => {
    if (!v || v.status !== "pendiente" || !user) return false;
    if (user.role === "admin") return true;
    if (!["approver", "warehouse"].includes(user.role)) return false;
    if (v.authorizer_id && v.authorizer_id === user.id) return true;
    if (user.role === "approver" && user.department &&
        (v.department || "").trim().toLowerCase() === user.department.trim().toLowerCase()) {
      return true;
    }
    return !v.authorizer_id;
  };

  const canSupply = (v) => v && ["autorizado", "surtido_parcial"].includes(v.status) &&
    (user?.role === "admin" || user?.role === "warehouse");

  const openSupply = async (v) => {
    try {
      const { data } = await api.get(`/vales/${v.id}`);
      setSupplyVale(data);
      const sup = (data.supply_lines || []).map((sl) => ({
        item_number: sl.item_number,
        description: sl.description,
        uom: sl.uom,
        warehouse: sl.warehouse,
        qty_requested: sl.qty_requested,
        qty_already: sl.qty_delivered || 0,
        supply_status: sl.supply_status || "pending",
        qty_delivered: (sl.supply_status === "pending" || sl.supply_status === "partial")
          ? Math.max(sl.qty_requested - (sl.qty_delivered || 0), 0) : 0,
        rejected: false,
        reject_reason: "",
      }));
      setSupplyLines(sup);
      setSupplyOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitSupply = async () => {
    if (!supplyVale) return;
    try {
      const payload = {
        lines: supplyLines
          .filter((l) => l.supply_status !== "supplied" && l.supply_status !== "rejected")
          .map((l) => ({
            item_number: l.item_number,
            qty_delivered: Number(l.qty_delivered) || 0,
            rejected: !!l.rejected,
            reject_reason: l.reject_reason || "",
          })),
      };
      const { data } = await api.post(`/vales/${supplyVale.id}/supply`, payload);
      toast.success(`Vale ${data.number} · ${ST_LABELS[data.status] || data.status}`);
      setSupplyOpen(false);
      setSupplyVale(null);
      load();
      if (detail?.id === supplyVale.id) setDetail(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="vales-page">
      <PageHeader title={t("vs.title")} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input
          data-testid="vales-search-input"
          placeholder={t("vs.searchPh")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs rounded-none h-9 text-xs"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none h-9 w-44 text-xs" data-testid="vales-status-filter">
            <SelectValue placeholder={t("common.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">— Estatus —</SelectItem>
            {Object.entries(ST_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          {canCreate && (
            <Button size="sm" className="rounded-none text-xs font-bold" onClick={openNew} data-testid="vales-new-btn">
              <Plus size={12} className="mr-1" /> {t("vs.new")}
            </Button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty text={t("common.noData")} />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="vales-table">
            <thead><tr>
              <th>{t("vs.folio")}</th>
              <th>{t("vs.date")}</th>
              <th>{t("vs.department")}</th>
              <th>{t("vs.requester")}</th>
              <th>{t("vs.generator")}</th>
              <th>{t("vs.authorizer")}</th>
              <th>{t("vs.lines")}</th>
              <th>{t("common.status")}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} data-testid={`vale-row-${v.id}`} className="cursor-pointer hover:bg-muted"
                    onClick={() => openDetail(v)}>
                  <td className="font-bold">{v.number}</td>
                  <td className="text-muted-foreground">{fmtDate(v.created_at)}</td>
                  <td>{v.department}</td>
                  <td>{v.requester_name || "—"}</td>
                  <td className="text-muted-foreground text-[11px]">{v.generator_email}</td>
                  <td className="text-muted-foreground text-[11px]">
                    {v.authorized_by || v.authorizer_email || "—"}
                  </td>
                  <td className="num">{(v.lines || []).length}</td>
                  <td><StatusBadge status={ST_STATUS_MAP[v.status] || v.status} label={ST_LABELS[v.status]} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      {canSupply(v) && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-emerald-700 font-bold"
                                onClick={() => openSupply(v)} data-testid={`vale-supply-${v.id}`}
                                title="Surtir">
                          Surtir
                        </Button>
                      )}
                      {(v.status === "autorizado" || v.status === "surtido") && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                                onClick={() => printPdf(v)} data-testid={`vale-print-${v.id}`}>
                          <Printer size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY_FORM); setEditId(null); } }}>
        <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? t("vs.edit") : t("vs.new")}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-bold uppercase">{t("vs.date")}</Label>
              <Input value={new Date().toLocaleDateString("es-MX")} readOnly disabled
                     className="rounded-none h-9 text-xs bg-muted" data-testid="vale-form-date" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase">{t("vs.generator")}</Label>
              <Input value={user?.email || ""} readOnly disabled
                     className="rounded-none h-9 text-xs bg-muted" data-testid="vale-form-generator" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase">{t("vs.department")} *</Label>
              <Select value={form.department || "_none"} onValueChange={(v) => {
                if (v === "_none") { setForm({ ...form, department: "" }); return; }
                const d = departments.find((x) => x.name === v);
                const defaultWh = d?.default_warehouse || "";
                // Pre-fill warehouse in all lines if empty
                const newLines = form.lines.map((ln) => (
                  ln.warehouse ? ln : { ...ln, warehouse: defaultWh }
                ));
                setForm({ ...form, department: v, lines: newLines });
              }}>
                <SelectTrigger className="rounded-none h-9 text-xs" data-testid="vale-form-dept">
                  <SelectValue placeholder={t("vs.department")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {departments.filter((d) => d.active !== false).map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label className="text-[10px] font-bold uppercase">{t("vs.requester")} *</Label>
              <Input
                placeholder="Buscar por nombre o número de empleado…"
                value={form.requester_name}
                onChange={(e) => {
                  const val = e.target.value;
                  const p = personnel.find((x) =>
                    x.employee_number === val.trim() ||
                    (x.name || "").toLowerCase() === val.trim().toLowerCase()
                  );
                  setForm({ ...form,
                    requester_name: val,
                    requester_id: p?.id || "",
                    requester_number: p?.employee_number || "" });
                }}
                list="requester-datalist"
                className="rounded-none h-9 text-xs" data-testid="vale-form-requester" />
              <datalist id="requester-datalist">
                {personnel.slice(0, 300).map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.employee_number ? `${p.employee_number} · ` : ""}{p.department || ""}
                  </option>
                ))}
              </datalist>
              {form.requester_id ? (
                <div className="text-[9px] text-emerald-700 mt-0.5">
                  ✓ {personnel.find((x) => x.id === form.requester_id)?.employee_number}
                </div>
              ) : form.requester_name ? (
                <div className="text-[9px] text-amber-700 mt-0.5">
                  Sin coincidencia · escribe nombre exacto o número
                </div>
              ) : null}
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase">{t("vs.authorizer")} ({t("common.optional")})</Label>
              <Select value={form.authorizer_id || "_none"} onValueChange={(v) => setForm({ ...form, authorizer_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-9 text-xs" data-testid="vale-form-authorizer">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— {t("common.any")} —</SelectItem>
                  {authorizers.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.email} · {a.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-full">
              <Label className="text-[10px] font-bold uppercase">{t("vs.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="rounded-none text-xs min-h-[60px]" rows={2} data-testid="vale-form-notes" />
            </div>
          </div>
          <div className="mt-2">
            <CostCodeHeader value={form} onChange={(patch) => setForm({ ...form, ...patch })} testid="vale-cc" />
          </div>

          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("vs.lines")}</div>
              <Button size="sm" variant="outline" className="rounded-none text-xs font-bold h-7" onClick={addLine} data-testid="vale-add-line">
                <Plus size={12} className="mr-1" /> {t("common.add")}
              </Button>
            </div>
            <div className="border">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-1 text-left w-64">{t("vs.item")}</th>
                    <th className="p-1 text-left">{t("common.description")}</th>
                    <th className="p-1 text-right w-20">{t("common.qty")}</th>
                    <th className="p-1 text-left w-16">{t("common.uom")}</th>
                    <th className="p-1 text-left w-28">{t("common.warehouse")}</th>
                    <th className="p-1 text-left w-52">{t("vs.equipment")}</th>
                    <th className="p-1 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((ln, idx) => (
                    <tr key={idx} className="border-t" data-testid={`vale-line-row-${idx}`}>
                      <td className="p-1">
                        <ItemSearch value={ln.item_number} onSelect={(it) => pickItem(idx, it)} />
                      </td>
                      <td className="p-1">
                        <Input value={ln.description} onChange={(e) => updateLine(idx, { description: e.target.value })}
                               className="rounded-none h-8 text-xs" data-testid={`vale-line-desc-${idx}`} />
                      </td>
                      <td className="p-1">
                        <Input type="number" min={0.01} step="any" value={ln.qty}
                               onChange={(e) => updateLine(idx, { qty: e.target.value })}
                               className="rounded-none h-8 text-xs text-right" data-testid={`vale-line-qty-${idx}`} />
                      </td>
                      <td className="p-1">
                        <Input value={ln.uom} onChange={(e) => updateLine(idx, { uom: e.target.value })}
                               className="rounded-none h-8 text-xs" data-testid={`vale-line-uom-${idx}`} />
                      </td>
                      <td className="p-1">
                        <Select value={ln.warehouse || warehouses[0]?.code || ""} onValueChange={(v) => updateLine(idx, { warehouse: v })}>
                          <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`vale-line-wh-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {warehouses.map((w) => (
                              <SelectItem key={w.code || w} value={w.code || w}>{w.code || w}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1">
                        <Select value={ln.equipment_code || "_none"} onValueChange={(v) => pickEquipment(idx, v === "_none" ? "" : v)}>
                          <SelectTrigger className={`rounded-none h-8 text-xs ${ln.is_refaccion && !ln.equipment_code ? "border-destructive" : ""}`}
                                         data-testid={`vale-line-eq-${idx}`}>
                            <SelectValue placeholder={ln.is_refaccion ? "Obligatorio · refacción" : "—"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">—</SelectItem>
                            {equipment.filter((e) => e.active !== false).map((e) => (
                              <SelectItem key={e.id} value={e.code}>{e.code} · {e.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {ln.is_refaccion && (
                          <div className="text-[9px] text-destructive font-bold mt-0.5">{t("vs.equipRequired")}</div>
                        )}
                        <div className="mt-1"><AccountPicker value={ln.cost_code} onChange={(patch) => updateLine(idx, { cost_code: { ...(ln.cost_code || {}), ...patch } })}
                          header={form} equipmentCode={ln.equipment_code} deptoCode={departments.find((d) => d.name === form.department || d.code === form.department)?.acc_code}
                          testid={`vale-line-cc-${idx}`} /></div>
                      </td>
                      <td className="p-1">
                        {form.lines.length > 1 && (
                          <Button size="sm" variant="ghost" className="h-7 px-1 text-destructive"
                                  onClick={() => removeLine(idx)} data-testid={`vale-line-remove-${idx}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                    onClick={() => { setOpen(false); setForm(EMPTY_FORM); setEditId(null); }}
                    data-testid="vale-form-cancel">
              {t("common.cancel")}
            </Button>
            <Button size="sm" className="rounded-none text-xs font-bold" onClick={save} data-testid="vale-form-save">
              {editId ? t("common.save") : t("vs.saveDraft")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={detailOpen} onOpenChange={(o) => { setDetailOpen(o); if (!o) setDetail(null); }}>
        <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="vale-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {t("vs.title")} · <span className="text-primary">{detail.number}</span>
                  <StatusBadge status={ST_STATUS_MAP[detail.status] || detail.status} label={ST_LABELS[detail.status]} />
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div>
                  <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.date")}</div>
                  <div>{fmtDate(detail.created_at)}</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.generator")}</div>
                  <div>{detail.generator_email}</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.department")}</div>
                  <div className="font-bold">{detail.department}</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.requester")}</div>
                  <div className="font-bold">{detail.requester_name || "—"} <span className="text-muted-foreground">{detail.requester_number ? `(${detail.requester_number})` : ""}</span></div>
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.authorizer")}</div>
                  <div>{detail.authorized_by || detail.authorizer_email || "—"}</div>
                </div>
                {detail.authorized_at && (
                  <div>
                    <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.authorizedAt")}</div>
                    <div>{fmtDate(detail.authorized_at)}</div>
                  </div>
                )}
              </div>

              {detail.notes && (
                <div className="border p-2 bg-muted/30">
                  <div className="text-[9px] font-bold uppercase text-muted-foreground">{t("vs.notes")}</div>
                  <div className="text-xs">{detail.notes}</div>
                </div>
              )}

              {detail.last_rejection_reason && (
                <div className="border border-destructive/40 p-2 bg-destructive/5">
                  <div className="text-[9px] font-bold uppercase text-destructive">{t("vs.rejectionReason")}</div>
                  <div className="text-xs">{detail.last_rejection_reason}</div>
                </div>
              )}

              <div className="border">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-1 text-left">{t("vs.item")}</th>
                      <th className="p-1 text-left">{t("common.description")}</th>
                      <th className="p-1 text-right">{t("common.qty")}</th>
                      <th className="p-1 text-left">{t("common.uom")}</th>
                      <th className="p-1 text-left">{t("common.warehouse")}</th>
                      <th className="p-1 text-left">{t("vs.equipment")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((ln, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1 font-bold">{ln.item_number}</td>
                        <td className="p-1">{ln.description}<div><CostCodeChip cc={ln.cost_code} testid={`vale-detail-cc-${i}`} /></div></td>
                        <td className="p-1 text-right">{Number(ln.qty).toFixed(2)}</td>
                        <td className="p-1">{ln.uom}</td>
                        <td className="p-1">{ln.warehouse}</td>
                        <td className="p-1">
                          {ln.equipment_code ? `${ln.equipment_code}${ln.equipment_name ? ` · ${ln.equipment_name}` : ""}` : "—"}
                          {ln.is_refaccion && <span className="ml-1 text-[9px] font-bold uppercase text-primary">REF</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detail.history?.length > 0 && (
                <details className="text-[11px] border p-2">
                  <summary className="font-bold uppercase tracking-widest cursor-pointer text-muted-foreground">
                    {t("common.history")} ({detail.history.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {detail.history.map((h, i) => (
                      <li key={i} className="flex gap-2 text-muted-foreground">
                        <span className="font-bold uppercase">{h.action}</span>
                        <span>{h.by}</span>
                        <span>{fmtDate(h.at)}</span>
                        {h.reason && <span className="text-destructive">— {h.reason}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="flex flex-wrap gap-2 items-center pt-2 border-t">
                {detail.status === "autorizado" && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                            onClick={() => downloadPdf(detail)} data-testid="vale-detail-download-pdf">
                      <FileDown size={12} className="mr-1" /> {t("vs.downloadPdf")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                            onClick={() => printPdf(detail)} data-testid="vale-detail-print">
                      <Printer size={12} className="mr-1" /> {t("vs.print")}
                    </Button>
                  </>
                )}
                {canEditDraft(detail) && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                          onClick={() => openEdit(detail)} data-testid="vale-detail-edit">
                    <Edit3 size={12} className="mr-1" /> {t("common.edit")}
                  </Button>
                )}
                {detail.status === "en_captura" && detail.generator_email === user?.email && (
                  <Button size="sm" className="rounded-none text-xs font-bold"
                          onClick={() => doAction(detail.id, "submit")} data-testid="vale-detail-submit">
                    <Send size={12} className="mr-1" /> {t("vs.submit")}
                  </Button>
                )}
                {canAuthorizeThis(detail) && (
                  <>
                    <Button size="sm" className="rounded-none text-xs font-bold"
                            onClick={() => doAction(detail.id, "authorize")} data-testid="vale-detail-authorize">
                      <Check size={12} className="mr-1" /> {t("vs.authorize")}
                    </Button>
                    <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold"
                            onClick={() => { setReasonAction({ id: detail.id, type: "reject" }); setReasonText(""); }}
                            data-testid="vale-detail-reject">
                      <RotateCcw size={12} className="mr-1" /> {t("vs.reject")}
                    </Button>
                  </>
                )}
                {canCancel(detail) && (
                  <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold"
                          onClick={() => { setReasonAction({ id: detail.id, type: "cancel" }); setReasonText(""); }}
                          data-testid="vale-detail-cancel">
                    <Ban size={12} className="mr-1" /> {t("vs.cancel")}
                  </Button>
                )}
              </div>

              <EvidencePanel
                resourceType="vale"
                basePath={`/vales/${detail.id}/evidence`}
                canWrite={can("admin", "warehouse", "approver", "maintenance", "buyer")}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* REASON DIALOG (reject/cancel) */}
      <Dialog open={!!reasonAction} onOpenChange={(o) => { if (!o) { setReasonAction(null); setReasonText(""); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="vale-reason-dialog">
          <DialogHeader>
            <DialogTitle>
              {reasonAction?.type === "reject" ? t("vs.reject") : t("vs.cancel")}
            </DialogTitle>
          </DialogHeader>
          <Label className="text-[10px] font-bold uppercase">{t("vs.reason")}</Label>
          <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)}
                    className="rounded-none text-xs" rows={3} data-testid="vale-reason-input" />
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                    onClick={() => { setReasonAction(null); setReasonText(""); }}
                    data-testid="vale-reason-cancel">
              {t("common.cancel")}
            </Button>
            <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold"
                    onClick={() => doAction(reasonAction.id, reasonAction.type, { reason: reasonText })}
                    data-testid="vale-reason-confirm">
              {t("common.confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SUPPLY dialog (almacén surte el vale autorizado) */}
      <Dialog open={supplyOpen} onOpenChange={(o) => { setSupplyOpen(o); if (!o) { setSupplyVale(null); setSupplyLines([]); } }}>
        <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="vale-supply-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">
              Surtir Vale {supplyVale?.number}
            </DialogTitle>
          </DialogHeader>
          {supplyVale && (
            <>
              <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
                <div><b className="text-[9px] uppercase text-muted-foreground">Solicitante:</b><br/>{supplyVale.requester_name}</div>
                <div><b className="text-[9px] uppercase text-muted-foreground">Departamento:</b><br/>{supplyVale.department}</div>
                <div><b className="text-[9px] uppercase text-muted-foreground">Autorizado:</b><br/>{supplyVale.authorized_by}</div>
              </div>
              <div className="tbl-wrap">
                <table className="tbl text-xs">
                  <thead><tr>
                    <th>Artículo</th><th>Descripción</th><th>Almacén</th>
                    <th className="num">Solicitado</th><th className="num">Ya surtido</th>
                    <th className="num">A despachar</th><th>Rechazar</th>
                  </tr></thead>
                  <tbody>
                    {supplyLines.map((l, i) => {
                      const done = l.supply_status === "supplied" || l.supply_status === "rejected";
                      return (
                        <tr key={i} className={done ? "opacity-50" : ""}>
                          <td className="font-mono text-[10px]">{l.item_number}</td>
                          <td>{l.description}</td>
                          <td>{l.warehouse}</td>
                          <td className="num tabular-nums">{l.qty_requested}</td>
                          <td className="num tabular-nums text-muted-foreground">{l.qty_already || 0}</td>
                          <td className="num">
                            <Input type="number" min={0} step="any" disabled={done || l.rejected}
                              max={Math.max(l.qty_requested - (l.qty_already || 0), 0)}
                              value={l.qty_delivered}
                              onChange={(e) => setSupplyLines(supplyLines.map((x, xi) => xi === i ? { ...x, qty_delivered: e.target.value } : x))}
                              className="rounded-none h-7 text-[11px] tabular-nums font-bold w-20"
                              data-testid={`supply-qty-${l.item_number}`} />
                          </td>
                          <td>
                            {!done && (
                              <div className="flex items-center gap-1">
                                <input type="checkbox" checked={l.rejected}
                                  onChange={(e) => setSupplyLines(supplyLines.map((x, xi) => xi === i ? { ...x, rejected: e.target.checked, qty_delivered: e.target.checked ? 0 : x.qty_delivered } : x))}
                                  data-testid={`supply-reject-${l.item_number}`} />
                                {l.rejected && (
                                  <Input placeholder="Motivo" value={l.reject_reason}
                                    onChange={(e) => setSupplyLines(supplyLines.map((x, xi) => xi === i ? { ...x, reject_reason: e.target.value } : x))}
                                    className="rounded-none h-7 text-[10px] w-32" />
                                )}
                              </div>
                            )}
                            {done && <span className="text-[9px] text-muted-foreground uppercase">{l.supply_status}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setSupplyOpen(false)} className="rounded-none text-xs">Cancelar</Button>
                <Button onClick={submitSupply} className="rounded-none text-xs font-bold" data-testid="vale-supply-submit">
                  Confirmar surtido
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
